import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { Heading1Text } from '../../../components/Texts';
import { PHONE_MEDIA_QUERY } from '../../../common/responsive';
import { useGetGiftCardCatalogueQuery } from '../../../redux/api/public/globetopper';
import { getCurrencySymbol } from '../../../common/currency';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { NavigationBackButton } from '../../../components/BackButton/NavigationBackButton';
import SpendGalleryGiftCard from '../SpendGallery/SpendGalleryGiftCard';
import ScrollRestore from '../../../components/ScrollRestore';
import {
  selectGiftCardsForCountry,
  selectSelectedCountryCode,
} from '../../../redux/slices/giftCardCatalogue';

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  @media ${PHONE_MEDIA_QUERY} {
    padding: 20px;
    max-width: 100%;
  }
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const SpendTitle = styled(Heading1Text)`
  font-size: 24px;
  line-height: 28px;
  margin: 0;
  color: ${({ theme }) => theme.text.title};
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  @media ${PHONE_MEDIA_QUERY} {
    grid-template-columns: repeat(auto-fill, minmax(45%, 1fr));
    gap: 16px;
  }
`;

const EmptyState = styled.div`
  padding: 40px 0;
  text-align: center;
  color: ${({ theme }) => theme.text.secondary};
`;

const formatPriceRange = (card) => {
  if (!card) return '';
  if (card.detailedInfo?.min && card.detailedInfo?.max) {
    const min = Math.round(parseFloat(card.detailedInfo.min.replace(/,/g, '')));
    const max = Math.round(parseFloat(card.detailedInfo.max.replace(/,/g, '')));
    const currencySymbol = getCurrencySymbol(
      card.detailedInfo?.currency?.code || card.currency,
    );
    return `${currencySymbol}${min} - ${currencySymbol}${max}`;
  }
  if (!card.denomination) return '';
  const firstRange = card.denomination.split(' or ')[0];
  const match = firstRange.match(/([\d,]+\.?\d*)\s*-\s*([\d,]+\.?\d*)\s*by\s*[\d,]+\.?\d*/);
  if (!match) return card.denomination;
  const [, minStr, maxStr] = match;
  const min = Math.round(parseFloat(minStr.replace(/,/g, '')));
  const max = Math.round(parseFloat(maxStr.replace(/,/g, '')));
  const currencySymbol = getCurrencySymbol(card.currency);
  return `${currencySymbol}${min}-${currencySymbol}${max}`;
};

const SpendCategory = () => {
  const { categoryName } = useParams();
  const navigate = useNavigate();
  const selectedCountryCode = useSelector(selectSelectedCountryCode);
  const countryCode = selectedCountryCode || '';

  useGetGiftCardCatalogueQuery(countryCode);

  const giftCards = useSelector((state) =>
    selectGiftCardsForCountry(state, countryCode),
  );

  const categorySections = useMemo(() => {
    if (!giftCards || giftCards.length === 0) return [];
    const categoryMap = new Map();

    giftCards.forEach((card) => {
      const categories = card.detailedInfo?.categories;

      categories?.forEach((category) => {
        if (!category || category.id === undefined || category.id === null) {
          return;
        }

        const categoryId = category.id;
        if (!categoryMap.has(categoryId)) {
          categoryMap.set(categoryId, {
            category: {
              id: category.id,
              name: category.name,
              displayName: category.displayName || category.name,
            },
            cards: [],
            cardIds: new Set(),
          });
        }

        const entry = categoryMap.get(categoryId);
        const cardId = String(card.id);
        if (!entry.cardIds.has(cardId)) {
          entry.cardIds.add(cardId);
          entry.cards.push(card);
        }
      });
    });

    return Array.from(categoryMap.values()).map((section) => ({
      category: section.category,
      cards: section.cards,
    }));
  }, [giftCards]);

  const decodedName = decodeURIComponent(categoryName || '').toLowerCase();
  const activeSection = categorySections.find((section) => {
    const name = (section.category.displayName || section.category.name || '').toLowerCase();
    return name === decodedName;
  });

  if (!activeSection) {
    return (
      <Container>
        <NavigationBackButton onClick={() => navigate('/app/spend')} />
        <Header>
          <SpendTitle>Category not found</SpendTitle>
        </Header>
        <EmptyState>No cards found for this category.</EmptyState>
      </Container>
    );
  }

  const GiftCard = ({ card }) => (
    <SpendGalleryGiftCard card={card} priceRange={formatPriceRange(card)} />
  );

  const allCards = activeSection.cards;

  return (
    <Container>
      <ScrollRestore />
      <NavigationBackButton onClick={() => navigate('/app/spend')} />
      <Header>
        <SpendTitle>{activeSection.category.displayName}</SpendTitle>
      </Header>
      <GridContainer>
        {allCards.map((card) => (
          <GiftCard key={card.id} card={card} />
        ))}
      </GridContainer>
    </Container>
  );
};

export default SpendCategory;
